import { useEffect, useMemo, useRef, useState } from 'react';
import { emailFrameDocument } from './emailHtml';

interface EmailHtmlFrameProps {
  /** `html` from `sanitizeEmailHtml`, never the stored HTML. */
  sanitizedHtml: string;
  title: string;
}

/**
 * A mailbox email body in a sandboxed iframe (#3736). `allow-same-origin` only
 * lets this component measure the body height; without `allow-scripts` the
 * frame cannot use it, and every link opens with noopener.
 */
export function EmailHtmlFrame({ sanitizedHtml, title }: EmailHtmlFrameProps) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const srcDoc = useMemo(() => emailFrameDocument(sanitizedHtml), [sanitizedHtml]);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return undefined;
    const measure = () => {
      const body = frame.contentDocument?.body;
      if (body) setHeight(Math.min(Math.max(body.scrollHeight + 16, 60), 4000));
    };
    frame.addEventListener('load', measure);
    if (frame.contentDocument?.readyState === 'complete') measure();
    return () => frame.removeEventListener('load', measure);
  }, [srcDoc]);

  return (
    <iframe
      ref={ref}
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      style={{ width: '100%', height, border: 0, display: 'block', background: 'white' }}
    />
  );
}
